/**
 * Browser Web Speech API dictation (STT) for Ops Brain chat.
 * Mic starts only on user gesture. Falls back gracefully when unsupported.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

function getSpeechRecognitionCtor() {
  if (typeof window === 'undefined') return null;
  const w = window;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export function isSpeechDictationSupported() {
  return !!getSpeechRecognitionCtor();
}

export default function useSpeechDictation({
  lang = 'en-IN',
  onFinalTranscript,
  onInterimTranscript
} = {}) {
  const [supported] = useState(() => isSpeechDictationSupported());
  const [listening, setListening] = useState(false);
  const [error, setError] = useState('');
  const recognitionRef = useRef(null);
  const onFinalRef = useRef(onFinalTranscript);
  const onInterimRef = useRef(onInterimTranscript);

  useEffect(() => {
    onFinalRef.current = onFinalTranscript;
    onInterimRef.current = onInterimTranscript;
  }, [onFinalTranscript, onInterimTranscript]);

  const stop = useCallback(() => {
    const current = recognitionRef.current;
    recognitionRef.current = null;
    try {
      if (current) {
        current.onend = null;
        current.onerror = null;
        current.stop?.();
      }
    } catch {
      // ignore
    }
    setListening(false);
  }, []);

  useEffect(() => () => stop(), [stop]);

  const start = useCallback(() => {
    setError('');
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      setError('Voice input is not supported in this browser');
      return;
    }
    stop();
    const recognition = new Ctor();
    recognition.lang = lang || 'en-IN';
    recognition.interimResults = true;
    recognition.continuous = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setListening(true);
    recognition.onerror = (event) => {
      if (recognitionRef.current !== recognition) return;
      const code = event?.error || 'speech_error';
      if (code !== 'aborted' && code !== 'no-speech') {
        setError(`Mic error: ${code}`);
      }
      setListening(false);
    };
    recognition.onend = () => {
      if (recognitionRef.current !== recognition) return;
      setListening(false);
      recognitionRef.current = null;
    };
    recognition.onresult = (event) => {
      let interim = '';
      let finalText = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const text = result?.[0]?.transcript || '';
        if (result.isFinal) finalText += text;
        else interim += text;
      }
      if (interim && onInterimRef.current) onInterimRef.current(interim);
      if (finalText.trim() && onFinalRef.current) {
        onFinalRef.current(finalText.trim());
      }
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch (err) {
      setError(err?.message || 'Could not start microphone');
      setListening(false);
    }
  }, [lang, stop]);

  const toggle = useCallback(() => {
    if (listening) stop();
    else start();
  }, [listening, start, stop]);

  return { supported, listening, error, start, stop, toggle };
}
